import React, { useState, useEffect } from 'react';

interface Characteristic {
  id: number;
  name: string;
}

interface CharacteristicsFilterModalProps {
  show: boolean;
  selectedCharacteristicIds: number[];
  onClose: () => void;
  onApply: (characteristicIds: number[]) => void;
}

const CharacteristicsFilterModal: React.FC<CharacteristicsFilterModalProps> = ({
  show,
  selectedCharacteristicIds,
  onClose,
  onApply
}) => {
  const [allCharacteristics, setAllCharacteristics] = useState<Characteristic[]>([]);
  const [tempSelected, setTempSelected] = useState<number[]>(selectedCharacteristicIds);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setTempSelected(selectedCharacteristicIds);
      setSearchTerm('');
      // Fetch characteristics only when the modal is shown and data is not yet loaded
      if (allCharacteristics.length === 0) {
        setIsLoading(true);
        fetch('/api/v1/characteristics')
          .then(response => response.json())
          .then(data => {
            setAllCharacteristics(data);
            setIsLoading(false);
          })
          .catch(error => {
            console.error('Failed to fetch characteristics:', error);
            setIsLoading(false);
          });
      }
    }
  }, [show, selectedCharacteristicIds]);

  const handleToggle = (characteristicId: number) => {
    const newSelected = tempSelected.includes(characteristicId)
      ? tempSelected.filter(id => id !== characteristicId)
      : [...tempSelected, characteristicId];
    
    setTempSelected(newSelected);
  };

  const handleSelectAll = () => {
    setTempSelected(filteredCharacteristics.map(characteristic => characteristic.id));
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  const filteredCharacteristics = allCharacteristics.filter(characteristic => 
    characteristic.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!show) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10060 
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10061,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        tabIndex={-1}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">特徴を選択</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="閉じる"
              />
            </div>
            
            <div className="modal-body">
              {/* 選択/解除ボタン */}
              <div className="d-flex gap-2 mb-3">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSelectAll}
                >
                  一括選択
                </button>
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleClearAll}
                >
                  一括解除
                </button>
                <div className="ms-auto text-muted small">
                  {tempSelected.length}件選択中
                </div>
              </div>

              {/* 検索フィールド */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="特徴を検索... (例: 急募、フレックス、長期案件)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : (
                <div className="row g-2">
                  {filteredCharacteristics.map(characteristic => (
                    <div key={characteristic.id} className="col-6 col-md-4 col-lg-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`characteristic-${characteristic.id}`}
                          checked={tempSelected.includes(characteristic.id)}
                          onChange={() => handleToggle(characteristic.id)}
                        />
                        <label className="form-check-label" htmlFor={`characteristic-${characteristic.id}`} style={{ cursor: 'pointer' }}>
                          {characteristic.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleApply}
                disabled={isLoading}
              >
                この条件で検索
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CharacteristicsFilterModal;
